/**
 * Module Registry
 * Defines all available modules with their node types, edge types, and routes.
 * Modules are enabled/disabled per tenant via tenants.module_config
 */

export interface RouteDefinition {
  path: string;
  nodeType: string;
  label: string;
  icon: string;
  filter?: Record<string, string>;
}

export interface ModuleDefinition {
  id: string;
  name: string;
  description: string;
  category: string; // References CATEGORY_CONFIG id
  nodeTypes: string[];
  routes: RouteDefinition[];
  hasDashboard?: boolean;
  dashboardPath?: string;
  hasAnalytics?: boolean;
  dependencies?: string[];
}

export const MODULE_REGISTRY: Record<string, ModuleDefinition> = {
  'base-crm': {
    id: 'base-crm',
    name: 'Base CRM',
    description: 'Organizations, people, activities, tasks, and documents',
    category: 'crm',
    nodeTypes: ['Person', 'Organization', 'Activity', 'Task', 'Document'],
    routes: [
      { path: '/people', nodeType: 'Person', label: 'People', icon: 'User' },
      { path: '/organizations', nodeType: 'Organization', label: 'Organizations', icon: 'Building2' },
      { path: '/activities', nodeType: 'Activity', label: 'Activities', icon: 'MessageSquare' },
      { path: '/tasks', nodeType: 'Task', label: 'Tasks', icon: 'CheckSquare' },
      { path: '/documents', nodeType: 'Document', label: 'Documents', icon: 'FileText' },
    ],
  },
  sales: {
    id: 'sales',
    name: 'Sales',
    description: 'Opportunities and pipeline management',
    category: 'sales',
    nodeTypes: ['Opportunity'],
    routes: [
      { path: '/opportunities/pipeline', nodeType: 'Opportunity', label: 'Pipeline', icon: 'Kanban' },
      { path: '/opportunities', nodeType: 'Opportunity', label: 'Opportunities', icon: 'BriefcaseBusiness' },
    ],
    dependencies: ['base-crm'],
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Campaigns, lists, and events',
    category: 'marketing',
    nodeTypes: ['Campaign', 'Event'],
    routes: [
      { path: '/lists', nodeType: '', label: 'Lists', icon: 'ListChecks' },
      { path: '/campaigns', nodeType: 'Campaign', label: 'Campaigns', icon: 'Mail' },
      { path: '/events', nodeType: 'Event', label: 'Events', icon: 'Calendar' },
    ],
    dependencies: ['base-crm'],
  },
  'pevc-fundraising': {
    id: 'pevc-fundraising',
    name: 'PE/VC Fundraising',
    description: 'Vehicles, commitments, LPs',
    category: 'fundraising',
    nodeTypes: ['Vehicle', 'FundraisingOpportunity'],
    routes: [
      { path: '/vehicles', nodeType: 'Vehicle', label: 'Vehicles', icon: 'Landmark' },
      {
        path: '/fundraising/pipeline',
        nodeType: 'FundraisingOpportunity',
        label: 'LP Pipeline',
        icon: 'Kanban',
      },
      {
        path: '/fundraising',
        nodeType: 'FundraisingOpportunity',
        label: 'Fundraising',
        icon: 'HandCoins',
      },
    ],
    hasDashboard: true,
    dashboardPath: '/fundraising/dashboard',
    hasAnalytics: true,
    dependencies: ['base-crm'],
  },
  'pevc-ir': {
    id: 'pevc-ir',
    name: 'PE/VC Investor Relations',
    description: 'Commitments, capital calls, distributions, reports',
    category: 'ir',
    nodeTypes: ['InvestmentAccount', 'Commitment', 'CapitalCall', 'Distribution', 'Report'],
    routes: [
      {
        path: '/investment-accounts',
        nodeType: 'InvestmentAccount',
        label: 'Investment Accounts',
        icon: 'Wallet',
      },
      {
        path: '/commitments',
        nodeType: 'Commitment',
        label: 'Commitments',
        icon: 'FileSignature',
      },

      {
        path: '/capital-calls',
        nodeType: 'CapitalCall',
        label: 'Capital Calls',
        icon: 'ArrowUpCircle',
      },
      {
        path: '/distributions',
        nodeType: 'Distribution',
        label: 'Distributions',
        icon: 'ArrowDownCircle',
      },
      {
        path: '/reports',
        nodeType: 'Report',
        label: 'Reports',
        icon: 'FileText',
      },
    ],
    hasDashboard: true,
    dashboardPath: '/ir/dashboard',
    hasAnalytics: true,
    dependencies: ['base-crm', 'pevc-fundraising'],
  },
  'pevc-dealflow': {
    id: 'pevc-dealflow',
    name: 'PE/VC Deal Flow',
    description: 'Deals and due diligence',
    category: 'dealflow',
    nodeTypes: ['Deal'],
    routes: [
      { path: '/deals/pipeline', nodeType: 'Deal', label: 'Pipeline', icon: 'Kanban' },
      { path: '/deals', nodeType: 'Deal', label: 'Deals', icon: 'Handshake' },
    ],
    hasDashboard: true,
    dashboardPath: '/dealflow/dashboard',
    hasAnalytics: true,
    dependencies: ['base-crm'],
  },
  'pevc-portfolio': {
    id: 'pevc-portfolio',
    name: 'PE/VC Portfolio',
    description: 'Portfolio monitoring and metrics',
    category: 'portfolio',
    nodeTypes: ['Investment', 'ExternalFund'],
    routes: [
      {
        path: '/portfolio-companies',
        nodeType: 'Organization',
        label: 'Portfolio Companies',
        icon: 'Building2',
        filter: { type: 'portfolio_company' },
      },
      { path: '/investments', nodeType: 'Investment', label: 'Investments', icon: 'TrendingUp' },
      {
        path: '/external-funds',
        nodeType: 'ExternalFund',
        label: 'External Funds',
        icon: 'Briefcase',
      },
    ],
    hasDashboard: true,
    dashboardPath: '/portfolio/dashboard',
    hasAnalytics: true,
    dependencies: ['base-crm'],
  },
  pmn: {
    id: 'pmn',
    name: 'PMN',
    description: 'Properties, Metrics, and Narrative',
    category: 'crm',
    nodeTypes: ['Property', 'Metric', 'Narrative'],
    routes: [],
  },
};

export function getModule(moduleId: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY[moduleId];
}

export function getAllModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY);
}

export function getNodeTypeModule(nodeType: string): string | undefined {
  for (const [moduleId, module] of Object.entries(MODULE_REGISTRY)) {
    if (module.nodeTypes.includes(nodeType)) {
      return moduleId;
    }
  }
  return undefined;
}
